import { IF, NO, UITextView, UIView } from "uicore-ts"
import { CBColumnDescriptor, CBDataViewCellDescriptorWithObject } from "../../Custom components/CB/CBDataView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHDataView } from "../../Custom components/RH/RHDataView"
import { RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementPartDisplay } from "../../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import type { RHCPVSummationSpendItem } from "../../SocketClientFunctions"

export interface CPVSummationContractGroup {
    title: string
    amount: number
    percentage: number
    items: RHCPVSummationSpendItem[]
}

export class CPVSummationContractGroupView extends UIView {

    readonly toggleButton = new RHButton().configuredWithObject({
        variant: "light", contentPadding: RHSpacing.small,
        titleLabel: { isSingleLine: NO, textAlignment: UITextView.textAlignment.left,
            style: { fontSize: RHFont.sizeSmall, lineHeight: "1.5" } }
    }).addedAsSubviewToView(this)

    readonly dataView = new RHDataView<RHCPVSummationSpendItem>().configuredWithObject({
        isSearchAvailable: NO, hasResizableColumns: NO,
        maximumIntrinsicContentHeight: 500,
        tableView: { allRowsHaveEqualHeight: NO },
        style: { border: "none", boxShadow: "none" }
    })

    readonly moreButton = new RHButton().configuredWithObject({
        variant: "link", contentPadding: RHSpacing.small,
        titleLabel: { style: { fontSize: RHFont.sizeSmall } }
    })

    _group?: CPVSummationContractGroup
    _isExpanded = NO
    _areAllContractsVisible = NO

    constructor(elementID?: string) {
        super(elementID)
        this.dataView.descriptors = this._tableDescriptors()
        this.toggleButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._isExpanded = !this._isExpanded
            this._updateContractVisibility()
        }
        this.moreButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._areAllContractsVisible = !this._areAllContractsVisible
            this._updateContractVisibility()
        }
    }

    updateWithGroup(group: CPVSummationContractGroup) {
        this._group = group
        this._isExpanded = NO
        this._areAllContractsVisible = NO
        this._updateContractVisibility()
    }

    _updateContractVisibility() {
        const group = this._group
        if (!group) {
            return
        }
        const disclosure = IF(this._isExpanded)(() => "▾").ELSE(() => "▸")
        this.toggleButton.titleLabel.text = `${disclosure} ${RHTextSanitizer.escapedHTML(group.title)} · ` +
            CPVSummationContractGroupView.summaryTextForGroup(group)
        this.toggleButton.viewHTMLElement.setAttribute("aria-expanded", String(this._isExpanded))
        if (this._isExpanded) {
            this.dataView.data = IF(this._areAllContractsVisible)(() => group.items).ELSE(() => group.items.slice(0, 5))
            this.dataView.addedAsSubviewToView(this)
        }
        else {
            this.dataView.removeFromSuperview()
        }
        if (this._isExpanded && group.items.length > 5) {
            this.moreButton.titleLabel.text = IF(this._areAllContractsVisible)(() => "Näita vähem").ELSE(() =>
                `veel ${group.items.length - 5} lepingut`
            )
            this.moreButton.addedAsSubviewToView(this)
        }
        else {
            this.moreButton.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }

    static summaryTextForGroup(group: CPVSummationContractGroup): string {
        const contractText = IF(group.items.length === 1)(() => "1 leping").ELSE(() => `${group.items.length} lepingut`)
        return `${RHEuroFormatter.formattedAmount(group.amount)} · ${Math.round(group.percentage)} % · ${contractText}`
    }

    static titleForContractItem(item: RHCPVSummationSpendItem): string {
        let title = item.title || "Nimetu hange"
        if (item.partNumber !== undefined && RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(item.procurementPartCount)) {
            title += ` (osa ${item.partNumber})`
        }
        return title
    }

    static estonianDateTextForISODate(isoDate?: string): string {
        if (!isoDate) {
            return "–"
        }
        const date = new Date(isoDate)
        if (!Number.isFinite(date.getTime())) {
            return "–"
        }
        return new Intl.DateTimeFormat("et-EE", {
            timeZone: "Europe/Tallinn", day: "2-digit", month: "2-digit", year: "numeric"
        }).format(date)
    }

    _tableDescriptors(): CBColumnDescriptor<RHCPVSummationSpendItem>[] {
        return [
            CBDataViewCellDescriptorWithObject<RHCPVSummationSpendItem>({
                title: "Sõlmitud", keyPath: "commitmentDate", allowSorting: NO, minimumWidth: 105, weight: 1,
                updateCell: (cell, item) => {
                    cell.titleLabel!.text = CPVSummationContractGroupView.estonianDateTextForISODate(item.commitmentDate)
                }
            }),
            CBDataViewCellDescriptorWithObject<RHCPVSummationSpendItem>({
                title: "Leping", keyPath: "title", allowSorting: NO, minimumWidth: 190, weight: 4,
                updateCell: (cell, item) => {
                    cell.configureAsWrappingMultilineText()
                    cell.titleLabel!.text = RHTextSanitizer.escapedHTML(CPVSummationContractGroupView.titleForContractItem(item))
                }
            }),
            CBDataViewCellDescriptorWithObject<RHCPVSummationSpendItem>({
                title: "Maksumus", keyPath: "amountExclVat", allowSorting: NO, minimumWidth: 125, weight: 1.5,
                alignment: UITextView.textAlignment.right,
                updateCell: (cell, item) => { cell.titleLabel!.text = RHEuroFormatter.formattedAmount(item.amountExclVat) }
            })
        ]
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)
        bounds
            .rectangleWithHeight(Math.max(36, this.toggleButton.titleLabel.intrinsicContentHeight(
                bounds.width - RHSpacing.small * 2
            ) + RHSpacing.small * 2))
            .assignedAsFrameOfView(this.toggleButton)
            .IF(this._isExpanded)
            .rectangleForNextRow(RHSpacing.tiny, this.dataView)
            .assignedAsFrameOfView(this.dataView)
            .IF(this.moreButton.superview === this)
            .rectangleForNextRow(RHSpacing.tiny, 36)
            .assignedAsFrameOfView(this.moreButton)
            .ENDIF()
            .ENDIF()
    }
}
